import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { getTulpiaanToken } from '../utils/token';

// =============================================================================
// GESPREKSKLAAR — PROMPT
// =============================================================================

const PROMPT = `Je bent een carrièrecoach bij Tulpiaan. Je helpt iemand om zich optimaal voor te bereiden op een sollicitatiegesprek door slimme, doordachte vragen te formuleren die ze zelf kunnen stellen aan de interviewer.

TOON EN STIJL:
- Schrijf vragen zoals je ze in een echt gesprek stelt. Direct, menselijk, zonder omhaal.
- Gebruik altijd "je" en "jij", nooit "u".
- Korte zinnen. Splits lange vragen op.
- Geen jargon, geen formele formuleringen.
- Goede voorbeeldvraag: "Hoe ziet succes eruit in de eerste drie maanden?"
- Slechte voorbeeldvraag: "Kunt u beschrijven welke prestatie-indicatoren gehanteerd worden voor de evaluatie van nieuwe medewerkers in het eerste kwartaal?"

KERNPRINCIPES:
1. Geen eigen framework-naam of acroniem — de autoriteit zit in de inhoud.
2. Elke vraag toont dat de kandidaat serieus nadenkt over de rol, niet alleen maar wil scoren.
3. Focus op concrete antwoorden die helpen beslissen: is dit de juiste baan?
4. Vragen die initiatief en regie tonen, niet passiviteit.

STRUCTUUR:

Categorie 1 — DE ROL (wat verwachten ze écht van jou?)
- Succes & verwachting: Hoe ziet succes eruit? Wat verwachten ze in 3-6 maanden? Hoe wordt geëvalueerd? Wat is de definitie van goed werk?
- Uitdaging & context: Wat is de grootste uitdaging in deze rol? Waarom staat de functie open? Wat heeft de vorige medewerker geleerd of gedaan?

Categorie 2 — BEDRIJF & TEAM (waar stap je in?)
- Cultuur & samenwerking: Hoe wordt er samengewerkt? Wat maakt dit team bijzonder? Hoe gaan ze om met fouten? Hoe ziet een typische week eruit?
- Richting & focus: Waar gaat het bedrijf naartoe? Welke prioriteiten liggen er de komende periode? Wat verandert er, en hoe beïnvloedt dat deze rol?

Categorie 3 — GESPREK STUREN (initiatief en regie pakken)
- Initiatief & regie: Vragen die laten zien dat de kandidaat proactief meedenkt, eigen ideeën heeft en verder kijkt dan de functieomschrijving.
- Afsluiting: Vragen om het gesprek goed af te sluiten, volgende stappen te verduidelijken en eventuele twijfels bij de interviewer weg te nemen.

OUTPUT INSTRUCTIES:
Genereer 15 tot 25 vragen, verdeeld over de 3 categorieën en 6 subcategorieën (minimaal 2 vragen per subcategorie).
Pas elke vraag aan op: de vacaturetekst, het bedrijf, de functietitel, de gesprekspartner en het gespreksnummer.
Vroege gesprekken (1e, 2e): meer oriënterend en verkennend. Latere gesprekken (3e+, eindgesprek): meer strategisch, concreet en afrondend.

Per vraag:
- De vraag zelf: conversationeel, direct, zoals je het in een gesprek zegt
- De waarom: 1 tot 2 zinnen, legt uit waarom deze vraag slim en relevant is om te stellen

Geef ALLEEN de volgende JSON terug, geen uitleg, geen markdown:
{
  "samenvatting": "3 tot 4 zinnen over hoe de kandidaat dit gesprek het beste aanvliegt, wat de focus moet zijn en welke indruk ze willen achterlaten",
  "rol": {
    "succes_en_verwachting": [{"vraag": "...", "waarom": "..."}],
    "uitdaging_en_context": [{"vraag": "...", "waarom": "..."}]
  },
  "bedrijf_en_team": {
    "cultuur_en_samenwerking": [{"vraag": "...", "waarom": "..."}],
    "richting_en_focus": [{"vraag": "...", "waarom": "..."}]
  },
  "gesprek_sturen": {
    "initiatief_en_regie": [{"vraag": "...", "waarom": "..."}],
    "afsluiting": [{"vraag": "...", "waarom": "..."}]
  }
}`;

// =============================================================================
// KLEUREN
// =============================================================================
const C = {
  goud:'#cbad74', goudDiep:'#a8894f', goudLicht:'#f0e6cc',
  wit:'#ffffff', papier:'#faf9f6', grijs50:'#f5f4f1',
  grijs100:'#edecea', grijs200:'#d8d6d0', grijs300:'#c0bdb5', grijs400:'#9e9b93',
  grijs600:'#6b6860', grijs800:'#3a3830', inkt:'#1e1c17',
  fout:'#c04a3a', foutBg:'#fdf2f0',
  tBlauw:'#e8f0f8', tBlauwRand:'#b8cfe8', tTekst:'#3a6090',
  uOranje:'#fdf0e8', uOranjeRand:'#e8c8a0', uTekst:'#8a5020',
  lGroen:'#edf5ee', lGroenRand:'#a8cca8', lTekst:'#3a7040',
};

const CAT_KLEUR = {
  rol:             { bg:C.tBlauw,  rand:C.tBlauwRand,  tekst:C.tTekst },
  bedrijf_en_team: { bg:C.uOranje, rand:C.uOranjeRand, tekst:C.uTekst },
  gesprek_sturen:  { bg:C.lGroen,  rand:C.lGroenRand,  tekst:C.lTekst },
};

const GROEP_LABELS = {
  succes_en_verwachting:   'Succes & verwachting',
  uitdaging_en_context:    'Uitdaging & context',
  cultuur_en_samenwerking: 'Cultuur & samenwerking',
  richting_en_focus:       'Richting & focus',
  initiatief_en_regie:     'Initiatief & regie',
  afsluiting:              'Afsluiting',
};

const CATS = [
  { key:'rol',             icon:'👤', label:'De rol',         sub:'Wat verwachten ze écht van jou?' },
  { key:'bedrijf_en_team', icon:'🏢', label:'Bedrijf & team', sub:'Waar stap je in?' },
  { key:'gesprek_sturen',  icon:'🎯', label:'Gesprek sturen', sub:'Initiatief en regie pakken' },
];

// =============================================================================
// PDF GENERATOR
// =============================================================================
function genereerPdfHtml(form, data, logoSrc) {
  const datum = new Date().toLocaleDateString('nl-NL', {day:'numeric', month:'long', year:'numeric'});

  const catKleuren = {
    rol:             {bg:'#e8f0f8', rand:'#b8cfe8', tekst:'#3a6090', lichtBg:'#f0f5fb'},
    bedrijf_en_team: {bg:'#fdf0e8', rand:'#e8c8a0', tekst:'#8a5020', lichtBg:'#fef7f2'},
    gesprek_sturen:  {bg:'#edf5ee', rand:'#a8cca8', tekst:'#3a7040', lichtBg:'#f4faf4'},
  };
  const catIcons  = { rol:'👤', bedrijf_en_team:'🏢', gesprek_sturen:'🎯' };
  const catLabels = { rol:'De rol', bedrijf_en_team:'Bedrijf & team', gesprek_sturen:'Gesprek sturen' };
  const catSubs   = { rol:'Wat verwachten ze écht van jou?', bedrijf_en_team:'Waar stap je in?', gesprek_sturen:'Initiatief en regie pakken' };

  const renderSectie = (catKey, catData) => {
    const fk = catKleuren[catKey];
    let nr = 1;
    let html = `
      <div style="display:flex; align-items:stretch; margin:20px 0 0 0; break-before:auto;">
        <div style="width:4px; background:${fk.rand}; flex-shrink:0; border-radius:2px 0 0 0;"></div>
        <div style="background:${fk.bg}; padding:10px 16px; flex:1; display:flex; align-items:center; gap:12px;">
          <span style="font-size:18px; line-height:1;">${catIcons[catKey]}</span>
          <div>
            <span style="font-family:Georgia,serif; font-size:15px; font-weight:600; color:${fk.tekst};">${catLabels[catKey]}</span>
            <span style="font-size:11px; color:#6b6860; margin-left:8px;">${catSubs[catKey]}</span>
          </div>
        </div>
      </div>`;

    Object.entries(catData).forEach(([groep, vragen]) => {
      const groepLabel = GROEP_LABELS[groep] || groep;
      html += `
        <table style="width:100%; border-collapse:collapse; margin-bottom:1px;">
          <thead>
            <tr style="background:${fk.lichtBg};">
              <td colspan="3" style="padding:5px 10px; border:1px solid ${fk.rand}; border-top:none;">
                <span style="font-size:9px; font-weight:700; color:${fk.tekst}; text-transform:uppercase; letter-spacing:0.12em;">${groepLabel}</span>
              </td>
            </tr>
            <tr style="background:#f5f4f1;">
              <th style="width:40%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none;">Vraag</th>
              <th style="width:30%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none; border-left:none;">Waarom je dit vraagt</th>
              <th style="width:30%; padding:5px 10px; text-align:left; font-size:9px; font-weight:700; color:#9e9b93; text-transform:uppercase; letter-spacing:0.06em; border:1px solid #edecea; border-top:none; border-left:none;">Notitie</th>
            </tr>
          </thead>
          <tbody>`;

      vragen.forEach((v, i) => {
        const bg = i % 2 === 0 ? '#ffffff' : '#fafaf8';
        html += `
            <tr style="background:${bg}; vertical-align:top; page-break-inside:avoid;">
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none;">
                <span style="font-size:9px; font-weight:700; color:#cbad74; margin-right:5px;">${String(nr).padStart(2,'0')}</span><span style="font-family:Georgia,serif; font-size:12px; color:#1e1c17; line-height:1.45;">${v.vraag}</span>
              </td>
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none; border-left:none;">
                <span style="font-size:10px; color:#9e9b93; font-style:italic; line-height:1.4;">${v.waarom}</span>
              </td>
              <td style="padding:6px 10px; border:1px solid #edecea; border-top:none; border-left:none; background:#fdfdf9; min-height:36px;"></td>
            </tr>`;
        nr++;
      });
      html += `</tbody></table>`;
    });
    return html;
  };

  const gesprekspartner = [form.gesprekspartnerNaam, form.gesprekspartnerRol].filter(Boolean).join(' — ');

  return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>Gespreksklaar — ${form.bedrijfsnaam || 'Sollicitatie'}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'DM Sans',sans-serif; color:#1e1c17; background:#fff; font-size:12px; line-height:1.4; }
  @media print {
    @page { margin:12mm 10mm; size:A4 portrait; }
    .no-print { display:none !important; }
    body { font-size:10px; }
    tr { page-break-inside:avoid; }
    table { page-break-inside:auto; }
  }
</style>
</head>
<body>

<div class="no-print" style="position:fixed; top:12px; right:12px; z-index:999; display:flex; gap:8px;">
  <button onclick="window.print()" style="background:#cbad74; color:#fff; border:none; border-radius:6px; padding:9px 18px; font-size:13px; font-weight:600; cursor:pointer; font-family:'DM Sans',sans-serif; box-shadow:0 2px 8px rgba(168,137,79,.3);">
    🖨 Afdrukken / Opslaan als PDF
  </button>
  <button onclick="window.close()" style="background:#f5f4f1; color:#6b6860; border:none; border-radius:6px; padding:9px 14px; font-size:13px; cursor:pointer; font-family:'DM Sans',sans-serif;">
    ✕
  </button>
</div>

<div style="padding:14px 24px 12px; border-bottom:2px solid #cbad74; display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
  <div style="display:flex; align-items:center; gap:16px;">
    <div>
      <div style="display:flex; align-items:center; gap:8px; margin-bottom:2px;">
        ${logoSrc ? `<img src="${logoSrc}" alt="Tulpiaan" style="width:24px; height:24px; object-fit:contain;" />` : `<span style="color:#cbad74; font-size:16px;">✦</span>`}
        <span style="font-size:9px; letter-spacing:0.3em; color:#a8894f; font-weight:700; text-transform:uppercase;">TULPIAAN</span>
      </div>
      <div style="font-family:Georgia,serif; font-size:18px; font-weight:600; color:#1e1c17; letter-spacing:-0.02em;">Gespreksklaar</div>
    </div>
    <div style="width:1px; height:32px; background:#edecea;"></div>
    <div style="display:flex; gap:20px; font-size:11px; flex-wrap:wrap;">
      ${form.bedrijfsnaam ? `<div><span style="color:#9e9b93;">Bedrijf:</span> <strong>${form.bedrijfsnaam}</strong></div>` : ''}
      ${form.functietitel ? `<div><span style="color:#9e9b93;">Functie:</span> <strong>${form.functietitel}</strong></div>` : ''}
      ${gesprekspartner   ? `<div><span style="color:#9e9b93;">Gesprekspartner:</span> <strong>${gesprekspartner}</strong></div>` : ''}
      ${form.gespreksnummer ? `<div><span style="color:#9e9b93;">Gesprek:</span> <strong>${form.gespreksnummer}</strong></div>` : ''}
    </div>
  </div>
  <div style="text-align:right; font-size:10px; color:#9e9b93;">${datum}</div>
</div>

<div style="padding:0 24px 24px;">

<div style="background:#fdf9f0; border-left:3px solid #cbad74; padding:10px 14px; margin-bottom:12px; display:flex; gap:12px; align-items:flex-start;">
  <div style="font-size:9px; font-weight:700; color:#a8894f; text-transform:uppercase; letter-spacing:0.12em; white-space:nowrap; padding-top:2px;">Gespreksstrategie</div>
  <div style="font-family:Georgia,serif; font-size:12px; line-height:1.6; color:#3a3830; font-style:italic;">${data.samenvatting}</div>
</div>

${renderSectie('rol', data.rol)}
${renderSectie('bedrijf_en_team', data.bedrijf_en_team)}
${renderSectie('gesprek_sturen', data.gesprek_sturen)}

<div style="margin-top:32px; padding-top:16px; border-top:1px solid #edecea; display:flex; justify-content:space-between; align-items:center;">
  <div style="font-size:10px; color:#9e9b93;">✦ Tulpiaan — Sollicitatievoorbereiding</div>
  <div style="font-size:10px; color:#9e9b93;">${datum} — Vertrouwelijk</div>
</div>

</div>
</body>
</html>`;
}

// =============================================================================
// TEKST GENERATOR (kopieer)
// =============================================================================
function genereerTekst(form, data) {
  const datum = new Date().toLocaleDateString('nl-NL', {day:'numeric', month:'long', year:'numeric'});
  let tekst = '';
  tekst += `GESPREKSKLAAR — Sollicitatievoorbereiding\n`;
  tekst += `${form.bedrijfsnaam || 'Bedrijf'} — ${form.functietitel || 'Functie'} — ${datum}\n`;
  tekst += `${'═'.repeat(60)}\n\n`;
  tekst += `GESPREKSSTRATEGIE\n${data.samenvatting}\n\n`;

  let nr = 1;
  CATS.forEach(cat => {
    tekst += `\n\n${cat.label.toUpperCase()} — ${cat.sub}\n`;
    Object.entries(data[cat.key] || {}).forEach(([groep, vragen]) => {
      tekst += `\n  ${(GROEP_LABELS[groep] || groep).toUpperCase()}\n`;
      vragen.forEach(v => {
        tekst += `\n  ${String(nr).padStart(2,'0')}. ${v.vraag}\n`;
        tekst += `\n`;
        nr++;
      });
    });
  });

  tekst += `\nTulpiaan — Sollicitatievoorbereiding\n`;
  return tekst;
}

// =============================================================================
// EXCEL GENERATOR
// =============================================================================
function genereerExcel(form, data) {
  const XLSX = window.XLSX;
  if (!XLSX) { alert('Excel-library laadt nog, probeer opnieuw.'); return; }

  const wb = XLSX.utils.book_new();
  const rijen = [];

  rijen.push(['', 'GESPREKSKLAAR — Sollicitatievoorbereiding', '']);
  rijen.push(['', data.samenvatting, '']);
  rijen.push(['', '', '']);

  let nr = 1;
  const catIcons = { rol:'👤', bedrijf_en_team:'🏢', gesprek_sturen:'🎯' };

  CATS.forEach(cat => {
    rijen.push([catIcons[cat.key], cat.label.toUpperCase() + ' — ' + cat.sub, 'Notitie:']);
    Object.entries(data[cat.key] || {}).forEach(([groep, vragen]) => {
      vragen.forEach(v => {
        rijen.push([String(nr).padStart(2, '0'), v.vraag, '']);
        nr++;
      });
    });
  });

  const ws = XLSX.utils.aoa_to_sheet(rijen);
  ws['!cols'] = [{ wch: 4.3 }, { wch: 61 }, { wch: 41 }];
  if (!ws['!rows']) ws['!rows'] = [];
  ws['!rows'][1] = { hpt: 67 };

  const getCellRef = (r, c) => XLSX.utils.encode_cell({ r, c });

  rijen.forEach((r, i) => {
    if (r[0] && isNaN(parseInt(r[0])) && r[0] !== '') {
      [0, 1].forEach(ci => {
        const ref = getCellRef(i, ci);
        if (ws[ref]) ws[ref].s = { font: { bold: true, name: 'Arial', sz: 11 } };
      });
    }
  });

  const titelRef = getCellRef(0, 1);
  if (ws[titelRef]) ws[titelRef].s = { font: { bold: true, name: 'Arial', sz: 12 } };

  XLSX.utils.book_append_sheet(wb, ws, 'Gespreksklaar');
  const bestandsnaam = `Gespreksklaar_${(form.bedrijfsnaam || 'Sollicitatie').replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`;
  XLSX.writeFile(wb, bestandsnaam);
}

// =============================================================================
// EXPORT PANEEL
// =============================================================================
function ExportPaneel({ form, data }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  const [xlsxKlaar, setXlsxKlaar]   = useState(!!window.XLSX);

  useEffect(() => {
    if (!window.XLSX) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      script.onload = () => setXlsxKlaar(true);
      document.head.appendChild(script);
    }
  }, []);

  const openPdf = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      const b64 = canvas.toDataURL('image/png');
      const html = genereerPdfHtml(form, data, b64);
      const venster = window.open('', '_blank', 'width=1200,height=800');
      if (venster) { venster.document.write(html); venster.document.close(); }
    };
    img.onerror = () => {
      const html = genereerPdfHtml(form, data, null);
      const venster = window.open('', '_blank', 'width=1200,height=800');
      if (venster) { venster.document.write(html); venster.document.close(); }
    };
    img.src = logo;
  };

  const kopieerAlles = () => {
    navigator.clipboard.writeText(genereerTekst(form, data));
    setGekopieerd(true);
    setTimeout(() => setGekopieerd(false), 2000);
  };

  return (
    <div style={xp.paneel}>
      <div style={xp.paneelKop}>
        <span style={xp.paneelIcon}>↗</span>
        <div>
          <div style={xp.paneelTitel}>Exporteren</div>
          <div style={xp.paneelSub}>PDF voor voorbereiding · Excel voor notities tijdens het gesprek · Kopieer voor andere tools</div>
        </div>
      </div>
      <div style={xp.rijen}>
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📄 PDF — Volledige voorbereiding</div>
            <div style={xp.rijSub}>Met gespreksstrategie, alle vragen, waarom-kolom en notitieruimte. Tulpiaan-opmaak. Afdrukken of opslaan als PDF.</div>
          </div>
          <button onClick={openPdf} style={xp.knopGoud} className="knop-outline">Openen / Afdrukken</button>
        </div>
        <div style={xp.divider}></div>
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📊 Excel — Notitieversie</div>
            <div style={xp.rijSub}>Alle vragen met lege notitiekolom. Invullen tijdens of na het gesprek.</div>
          </div>
          <button onClick={() => genereerExcel(form, data)} disabled={!xlsxKlaar}
            style={{ ...xp.knopGoud, opacity: xlsxKlaar ? 1 : 0.45, cursor: xlsxKlaar ? 'pointer' : 'not-allowed' }}
            className="knop-outline">
            {xlsxKlaar ? 'Download .xlsx' : 'Laden...'}
          </button>
        </div>
        <div style={xp.divider}></div>
        <div style={xp.rij}>
          <div style={xp.rijInfo}>
            <div style={xp.rijTitel}>📋 Kopieer — Platte tekst</div>
            <div style={xp.rijSub}>Plak direct in Notion, Word, je CRM of een andere tool.</div>
          </div>
          <button onClick={kopieerAlles} style={xp.knopGhostXp} className="knop-ghost">
            {gekopieerd ? '✓ Gekopieerd' : 'Kopieer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const xp = {
  paneel:     { background:C.wit, border:`1.5px solid ${C.grijs200}`, borderRadius:12, overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,.06)', marginTop:32 },
  paneelKop:  { background:C.grijs50, borderBottom:`1px solid ${C.grijs100}`, padding:'14px 22px', display:'flex', alignItems:'center', gap:12 },
  paneelIcon: { fontSize:18, color:C.goudDiep, fontWeight:700 },
  paneelTitel:{ fontSize:14, fontWeight:700, color:C.inkt },
  paneelSub:  { fontSize:12, color:C.grijs400 },
  rijen:      { padding:'4px 0' },
  rij:        { padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:20, flexWrap:'wrap' },
  rijInfo:    { flex:1, minWidth:200 },
  rijTitel:   { fontSize:13, fontWeight:600, color:C.inkt, marginBottom:3 },
  rijSub:     { fontSize:12, color:C.grijs400, lineHeight:1.5 },
  divider:    { height:1, background:C.grijs100, margin:'0 22px' },
  knopGoud:      { border:`1.5px solid ${C.goud}`, color:C.goudDiep, background:'transparent', borderRadius:7, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer', transition:'all .15s ease', whiteSpace:'nowrap' },
  knopGhostXp:   { border:`1.5px solid ${C.grijs200}`, color:C.grijs600, background:'transparent', borderRadius:7, padding:'9px 16px', fontSize:13, fontWeight:500, cursor:'pointer', transition:'all .15s ease', whiteSpace:'nowrap' },
};

// =============================================================================
// GLOBALE CSS
// =============================================================================
const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  input, textarea, button { font-family:'DM Sans',sans-serif; }
  input::placeholder, textarea::placeholder { color:${C.grijs400}; }
  input:focus, textarea:focus { outline:none; border-color:${C.goud} !important; box-shadow:0 0 0 3px ${C.goudLicht} !important; }
  textarea { resize:vertical; }
  @keyframes spin   { to { transform:rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:.4} }
  .kaart:hover       { border-color:${C.goud} !important; box-shadow:0 2px 12px rgba(203,173,116,.12) !important; }
  .vraagkaart:hover  { transform:translateY(-1px); box-shadow:0 4px 16px rgba(0,0,0,.07) !important; }
  .tab-btn:hover:not(.tab-actief) { background:${C.grijs100} !important; }
  .knop-primair:hover:not(:disabled) { background:${C.goudDiep} !important; box-shadow:0 4px 20px rgba(168,137,79,.35) !important; }
  .knop-primair:disabled { opacity:.45; cursor:not-allowed; }
  .knop-outline:hover { background:${C.goud} !important; color:white !important; }
  .knop-ghost:hover   { background:${C.grijs100} !important; color:${C.grijs800} !important; }
`;

// =============================================================================
// HOOFDCOMPONENT
// =============================================================================
export default function GespreksklaarTool() {
  const [stap, setStap] = useState('intake');
  const [fout, setFout] = useState(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    vacaturetekst:'', bedrijfsnaam:'', functietitel:'',
    gesprekspartnerNaam:'', gesprekspartnerRol:'',
    gespreksnummer:'', voorbereiding:'', context:'',
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const formValid = form.bedrijfsnaam.trim() && form.functietitel.trim() && form.gesprekspartnerRol.trim();

  const contextString = [
    `Bedrijf: ${form.bedrijfsnaam}`,
    `Functietitel: ${form.functietitel}`,
    form.gesprekspartnerNaam
      ? `Gesprekspartner: ${form.gesprekspartnerNaam} (${form.gesprekspartnerRol})`
      : `Gesprekspartner: ${form.gesprekspartnerRol}`,
    form.gespreksnummer   && `Hoeveelste gesprek: ${form.gespreksnummer}`,
    form.vacaturetekst    && `Vacaturetekst:\n${form.vacaturetekst}`,
    form.voorbereiding    && `Wat ze moeten voorbereiden: ${form.voorbereiding}`,
    form.context          && `Overige context: ${form.context}`,
  ].filter(Boolean).join('\n');

  const genereer = async () => {
    setStap('laden'); setFout(null);
    try {
      const token = await getTulpiaanToken();
      const res = await fetch('/api/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tulpiaan-token': token,
        },
        body: JSON.stringify({
          max_tokens: 4000,
          messages: [{ role: 'user', content: PROMPT + '\n\n' + contextString + '\n\nGenereer nu de vragenlijst. Alleen JSON.' }],
        }),
      });
      if (!res.ok) throw new Error('API ' + res.status);
      const json = await res.json();
      const tekst = json.content.filter(b => b.type === 'text').map(b => b.text).join('');
      setData(JSON.parse(tekst.replace(/```json|```/g, '').trim()));
      setStap('resultaat');
    } catch (e) {
      setFout('Er ging iets mis: ' + e.message);
      setStap('intake');
    }
  };

  const reset = () => { setData(null); setStap('intake'); };

  return (
    <>
      <style>{globalCss}</style>
      <div style={s.page}>
        <Header />
        <main style={s.main}>
          {stap !== 'intake' && <VoortgangsBalk stap={stap} />}
          {stap === 'intake'    && <Intake form={form} update={update} formValid={formValid} genereer={genereer} fout={fout} />}
          {stap === 'laden'     && <LaadScherm />}
          {stap === 'resultaat' && <Resultaat data={data} form={form} reset={reset} />}
        </main>
        <footer style={s.footer}>
          <span>Gespreksklaar</span><span style={{color:C.grijs200}}>·</span><span>Tulpiaan</span>
        </footer>
      </div>
    </>
  );
}

// =============================================================================
// HEADER
// =============================================================================
function Header() {
  return (
    <header style={s.header}>
      <div style={s.headerInner}>
        <div style={s.merk}>
          <img src={logo} alt="Tulpiaan logo" style={{width:32,height:32,objectFit:'contain'}} />
          <span style={s.merkNaam}>TULPIAAN</span>
        </div>
        <div>
          <h1 style={s.h1}>Gespreksklaar</h1>
          <p style={s.tagline}>Slimme vragen voor je <em style={s.em}>sollicitatiegesprek</em></p>
        </div>
      </div>
    </header>
  );
}

// =============================================================================
// VOORTGANGSBALK
// =============================================================================
function VoortgangsBalk({ stap }) {
  const fases = [
    { key:'intake',    label:'Voorbereiding', letter:'→' },
    { key:'resultaat', label:'Vragenlijst',   letter:'✦' },
  ];
  const actief = stap === 'laden' ? 'resultaat' : stap;
  const volgorde = ['intake','resultaat'];
  const actiefIdx = volgorde.indexOf(actief);

  return (
    <div style={s.voortgangsBalk}>
      {fases.map((f, i) => {
        const gedaan = i < actiefIdx, huidig = f.key === actief;
        return (
          <React.Fragment key={f.key}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{...s.voortgangsCirkel,...(huidig?{background:C.goud,color:C.wit,borderColor:C.goud}:{}),...(gedaan?{background:C.goudLicht,color:C.goudDiep,borderColor:C.goudLicht}:{})}}>
                {gedaan ? '✓' : f.letter}
              </div>
              <span style={{...s.voortgangsLabel,...(huidig?{color:C.inkt,fontWeight:600}:{})}}>{f.label}</span>
            </div>
            {i < fases.length - 1 && <div style={s.voortgangsLijn}></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// =============================================================================
// INTAKE
// =============================================================================
function Intake({ form, update, formValid, genereer, fout }) {
  return (
    <div style={{animation:'fadeUp .35s ease'}}>
      <div style={s.grid2}>
        <div style={s.kolom}>
          <FormSectie icon="📄" titel="De vacature">
            <Veld label="Vacaturetekst of link" placeholder="Plak hier de volledige vacaturetekst, of een link ernaar" waarde={form.vacaturetekst} onChange={v=>update('vacaturetekst',v)} multiline rows={6} hint="Hoe meer detail, hoe beter de vragen aansluiten op de rol" />
          </FormSectie>
          <FormSectie icon="🏢" titel="Bedrijf & functie">
            <Veld label="Naam van het bedrijf" placeholder="Bijv. ASML, Jumbo, Coolblue" waarde={form.bedrijfsnaam} onChange={v=>update('bedrijfsnaam',v)} verplicht />
            <Veld label="Functietitel" placeholder="Bijv. Product Manager, Sales Engineer, UX Designer" waarde={form.functietitel} onChange={v=>update('functietitel',v)} verplicht />
          </FormSectie>
        </div>
        <div style={s.kolom}>
          <FormSectie icon="🙋" titel="Het gesprek">
            <Veld label="Naam gesprekspartner" placeholder="Bijv. Sarah de Vries (optioneel)" waarde={form.gesprekspartnerNaam} onChange={v=>update('gesprekspartnerNaam',v)} />
            <Veld label="Rol gesprekspartner" placeholder="Bijv. HR manager, hiring manager, teamlead, directeur" waarde={form.gesprekspartnerRol} onChange={v=>update('gesprekspartnerRol',v)} verplicht />
            <Veld label="Het hoeveelste gesprek?" placeholder="Bijv. 1e kennismaking, 2e gesprek, eindgesprek" waarde={form.gespreksnummer} onChange={v=>update('gespreksnummer',v)} />
          </FormSectie>
          <FormSectie icon="💡" titel="Extra context (optioneel)">
            <Veld label="Wat moeten ze voorbereiden?" placeholder="Bijv. een casus, pitch, portfolio of presentatie" waarde={form.voorbereiding} onChange={v=>update('voorbereiding',v)} multiline />
            <Veld label="Overige context" placeholder="Bijv. wat je al weet over het bedrijf of wat je motiveert" waarde={form.context} onChange={v=>update('context',v)} multiline />
          </FormSectie>
        </div>
      </div>
      <div style={s.verplichtRij}><span style={{color:C.goud}}>*</span> Verplicht: bedrijfsnaam, functietitel en rol van de gesprekspartner</div>
      {fout && <div style={s.foutBox}>{fout}</div>}
      <div style={s.actieRij}>
        <button className="knop-primair" disabled={!formValid} onClick={genereer} style={s.knopPrimair}>
          Genereer vragenlijst <span style={{fontSize:18}}>→</span>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// RESULTAAT
// =============================================================================
function Resultaat({ data, form, reset }) {
  const [actief, setActief] = useState('rol');
  const totaal = CATS.reduce((a,c) => a + Object.values(data[c.key]||{}).reduce((b,arr) => b+arr.length, 0), 0);
  const actieveCat = CATS.find(c => c.key === actief);
  const fk = CAT_KLEUR[actief] || {};

  return (
    <div style={{animation:'fadeUp .35s ease'}}>
      <div style={s.resultaatHeader}>
        <div>
          <div style={s.resultaatBadge}>{totaal} vragen om te stellen</div>
          <h2 style={s.resultaatH2}>
            {form.bedrijfsnaam || 'Vragenlijst'}
            <span style={{color:C.grijs400,fontWeight:300}}> — {form.functietitel}</span>
          </h2>
        </div>
        <div style={s.resultaatActies}>
          <button className="knop-ghost" onClick={reset} style={s.knopGhost}>← Opnieuw</button>
        </div>
      </div>

      <div style={s.samenvattingBox}>
        <div style={s.samenvattingKop}><span style={s.samenvattingDot}></span>Gespreksstrategie</div>
        <p style={s.samenvattingTekst}>{data.samenvatting}</p>
      </div>

      <div style={s.tabBar}>
        {CATS.map(c => {
          const n = Object.values(data[c.key]||{}).reduce((a,arr) => a+arr.length, 0);
          const cfk = CAT_KLEUR[c.key];
          return (
            <button key={c.key} className={'tab-btn'+(actief===c.key?' tab-actief':'')}
              onClick={() => setActief(c.key)}
              style={{...s.tab,...(actief===c.key?{background:cfk.bg,border:`1.5px solid ${cfk.rand}`}:{})}}>
              <span style={{fontSize:20}}>{c.icon}</span>
              <div>
                <div style={{...s.tabLabel,color:actief===c.key?cfk.tekst:C.grijs600}}>{c.label}</div>
                <div style={s.tabSub}>{n} vragen</div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{animation:'fadeUp .25s ease'}}>
        <div style={{...s.catHeader,borderColor:fk.rand||C.grijs100}}>
          <span style={{fontSize:40,lineHeight:1}}>{actieveCat.icon}</span>
          <div>
            <h3 style={s.catTitel}>{actieveCat.label}</h3>
            <p style={s.catSub}>{actieveCat.sub}</p>
          </div>
        </div>
        {Object.entries(data[actief]||{}).map(([groep,vragen]) => (
          <Groep key={groep} naam={GROEP_LABELS[groep]||groep} vragen={vragen} accentKleur={fk.tekst} />
        ))}
      </div>

      <ExportPaneel form={form} data={data} />
    </div>
  );
}

// =============================================================================
// GEDEELDE COMPONENTEN
// =============================================================================
function Groep({ naam, vragen, accentKleur }) {
  return (
    <div style={s.groep}>
      <div style={s.groepKop}>
        <div style={{...s.groepDot,background:accentKleur||C.goud}}></div>
        <h4 style={s.groepTitel}>{naam}</h4>
        <div style={s.groepLijn}></div>
        <span style={{...s.groepTeller,color:accentKleur||C.goudDiep,background:(accentKleur||C.goudDiep)+'18'}}>{vragen.length}</span>
      </div>
      <div>{vragen.map((v,i) => <VraagKaart key={i} nr={i+1} vraag={v.vraag} waarom={v.waarom} accentKleur={accentKleur} />)}</div>
    </div>
  );
}

function VraagKaart({ nr, vraag, waarom, accentKleur }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="vraagkaart"
      style={{...s.vraagKaart,...(open?{border:`1px solid ${C.grijs200}`,boxShadow:'0 3px 12px rgba(0,0,0,.08)'}:{})}}
      onClick={() => setOpen(o => !o)}>
      <div style={s.vraagTop}>
        <span style={s.vraagNr}>{String(nr).padStart(2,'0')}</span>
        <p style={s.vraagTekst}>{vraag}</p>
        <span style={{...s.chevron,...(open?{transform:'rotate(180deg)'}:{})}}>▾</span>
      </div>
      {open && (
        <div style={s.waaromBlok}>
          <span style={{...s.waaromLabel,color:accentKleur||C.goudDiep}}>Waarom je dit vraagt</span>
          <p style={s.waaromTekst}>{waarom}</p>
        </div>
      )}
    </div>
  );
}

function FormSectie({ icon, titel, children }) {
  return (
    <div style={s.sectie}>
      <div style={s.sectieKop}><span style={s.sectieIcon}>{icon}</span><span style={s.sectieTitel}>{titel}</span></div>
      <div style={s.sectieBody}>{children}</div>
    </div>
  );
}

function Veld({ label, waarde, onChange, placeholder, verplicht, multiline, hint, rows }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div style={s.veld}>
      <label style={s.label}>{label}{verplicht && <span style={{color:C.goud}}> *</span>}</label>
      <Tag value={waarde} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows||2}
        style={{...s.input,...(multiline?{minHeight:64,lineHeight:1.5}:{})}} />
      {hint && <span style={s.hint}>{hint}</span>}
    </div>
  );
}

function LaadScherm() {
  const regels = ['Vacature analyseren','Gesprekspartner in kaart brengen','Strategische vragen opstellen','Toon afstemmen op het gesprek'];
  const [idx, setIdx] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const id = setInterval(() => setIdx(i => (i+1)%regels.length), 1800); return () => clearInterval(id); }, []);
  return (
    <div style={s.laden}>
      <div style={s.ladenCard}>
        <div style={s.spinner}></div>
        <p style={s.ladenTitel}>Vragenlijst genereren</p>
        <p style={s.ladenSub}>{regels[idx]}</p>
        <div style={s.voortbalkWrap}><div style={s.voortbalk}></div></div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================
const s = {
  page:           { minHeight:'100vh', background:C.papier, fontFamily:"'DM Sans',sans-serif", color:C.inkt, display:'flex', flexDirection:'column' },
  header:         { background:C.wit, borderBottom:`1px solid ${C.grijs100}`, padding:'0 32px' },
  headerInner:    { maxWidth:960, margin:'0 auto', padding:'24px 0', display:'flex', alignItems:'center', gap:24 },
  merk:           { display:'flex', flexDirection:'column', alignItems:'center', gap:4, borderRight:`1px solid ${C.grijs200}`, paddingRight:24, minWidth:80 },
  merkNaam:       { fontSize:10, letterSpacing:'0.25em', color:C.grijs600, fontWeight:600, textTransform:'uppercase' },
  h1:             { fontFamily:"'Lora',serif", fontSize:24, fontWeight:600, letterSpacing:'-0.02em', color:C.inkt },
  tagline:        { fontSize:13, color:C.grijs600, marginTop:3 },
  em:             { color:C.goudDiep, fontStyle:'italic' },
  main:           { flex:1, maxWidth:960, width:'100%', margin:'0 auto', padding:'36px 32px' },
  footer:         { textAlign:'center', padding:'18px 32px', fontSize:12, color:C.grijs400, borderTop:`1px solid ${C.grijs100}`, display:'flex', justifyContent:'center', gap:12 },
  voortgangsBalk: { display:'flex', alignItems:'center', marginBottom:36, background:C.wit, border:`1px solid ${C.grijs100}`, borderRadius:10, padding:'14px 24px', boxShadow:'0 1px 4px rgba(0,0,0,.04)' },
  voortgangsCirkel:{ width:32, height:32, borderRadius:'50%', border:`2px solid ${C.grijs200}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:C.grijs400, flexShrink:0 },
  voortgangsLabel:{ fontSize:13, color:C.grijs400 },
  voortgangsLijn: { flex:1, height:1, background:C.grijs200, margin:'0 12px' },
  grid2:          { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:20, alignItems:'start' },
  kolom:          { display:'flex', flexDirection:'column', gap:16 },
  sectie:         { background:C.wit, border:`1px solid ${C.grijs100}`, borderRadius:10, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.04)' },
  sectieKop:      { display:'flex', alignItems:'center', gap:10, padding:'13px 18px', borderBottom:`1px solid ${C.grijs100}`, background:C.grijs50 },
  sectieIcon:     { fontSize:15 },
  sectieTitel:    { fontSize:13, fontWeight:600, color:C.grijs800 },
  sectieBody:     { padding:'16px 18px', display:'flex', flexDirection:'column', gap:13 },
  veld:           { display:'flex', flexDirection:'column', gap:5 },
  label:          { fontSize:12, fontWeight:600, color:C.grijs600, letterSpacing:'0.02em' },
  input:          { border:`1.5px solid ${C.grijs200}`, borderRadius:7, padding:'10px 12px', fontSize:13, color:C.inkt, background:C.wit, transition:'all .15s ease', width:'100%' },
  hint:           { fontSize:11, color:C.grijs400, fontStyle:'italic' },
  verplichtRij:   { marginTop:16, fontSize:12, color:C.grijs400, textAlign:'right' },
  foutBox:        { background:C.foutBg, border:`1px solid ${C.fout}33`, color:C.fout, padding:'12px 16px', borderRadius:8, fontSize:13, marginTop:12 },
  actieRij:       { marginTop:28, display:'flex', justifyContent:'center' },
  knopPrimair:    { background:C.goud, color:C.wit, border:'none', borderRadius:8, padding:'13px 32px', fontSize:15, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:12, transition:'all .2s ease', boxShadow:'0 2px 12px rgba(168,137,79,.2)' },
  laden:          { display:'flex', justifyContent:'center', alignItems:'center', minHeight:300 },
  ladenCard:      { background:C.wit, border:`1px solid ${C.grijs100}`, borderRadius:16, padding:'44px 52px', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,.06)', minWidth:320 },
  spinner:        { width:40, height:40, border:`3px solid ${C.grijs100}`, borderTopColor:C.goud, borderRadius:'50%', margin:'0 auto 20px', animation:'spin 0.9s linear infinite' },
  ladenTitel:     { fontFamily:"'Lora',serif", fontSize:17, fontWeight:600, color:C.inkt, marginBottom:7 },
  ladenSub:       { fontSize:13, color:C.grijs400, animation:'pulse 1.8s ease-in-out infinite' },
  voortbalkWrap:  { marginTop:22, height:3, background:C.grijs100, borderRadius:2, overflow:'hidden' },
  voortbalk:      { height:'100%', width:'60%', background:`linear-gradient(90deg,${C.goudLicht},${C.goud})`, borderRadius:2, animation:'pulse 1.8s ease-in-out infinite' },
  resultaatHeader:{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:22, flexWrap:'wrap', gap:14 },
  resultaatBadge: { display:'inline-block', background:C.goudLicht, color:C.goudDiep, fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'4px 10px', borderRadius:20, marginBottom:7 },
  resultaatH2:    { fontFamily:"'Lora',serif", fontSize:24, fontWeight:600, letterSpacing:'-0.02em' },
  resultaatActies:{ display:'flex', gap:7, flexWrap:'wrap' },
  knopGhost:      { border:`1.5px solid ${C.grijs200}`, color:C.grijs600, background:'transparent', borderRadius:7, padding:'9px 15px', fontSize:13, fontWeight:500, cursor:'pointer', transition:'all .15s ease' },
  samenvattingBox:{ background:C.wit, border:`1px solid ${C.grijs100}`, borderLeft:`4px solid ${C.goud}`, borderRadius:'0 10px 10px 0', padding:'18px 22px', marginBottom:28, boxShadow:'0 1px 4px rgba(0,0,0,.04)' },
  samenvattingKop:{ display:'flex', alignItems:'center', gap:8, fontSize:11, fontWeight:700, color:C.goudDiep, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:9 },
  samenvattingDot:{ width:6, height:6, borderRadius:'50%', background:C.goud, display:'inline-block' },
  samenvattingTekst:{ fontFamily:"'Lora',serif", fontSize:14, lineHeight:1.7, color:C.grijs800, fontStyle:'italic' },
  tabBar:         { display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' },
  tab:            { flex:1, minWidth:140, display:'flex', alignItems:'center', gap:12, border:`1.5px solid ${C.grijs100}`, background:C.wit, borderRadius:9, padding:'10px 14px', cursor:'pointer', transition:'all .15s ease', color:C.grijs600 },
  tabLabel:       { fontSize:13, fontWeight:600 },
  tabSub:         { fontSize:11, color:C.grijs400 },
  catHeader:      { display:'flex', alignItems:'center', gap:18, marginBottom:24, paddingBottom:18, borderBottom:`2px solid ${C.grijs100}` },
  catTitel:       { fontFamily:"'Lora',serif", fontSize:20, fontWeight:600, color:C.inkt },
  catSub:         { fontSize:13, color:C.grijs400, marginTop:2 },
  groep:          { marginBottom:28 },
  groepKop:       { display:'flex', alignItems:'center', gap:9, marginBottom:10 },
  groepDot:       { width:7, height:7, borderRadius:'50%', flexShrink:0 },
  groepTitel:     { fontSize:11, fontWeight:700, color:C.grijs600, textTransform:'uppercase', letterSpacing:'0.08em' },
  groepLijn:      { flex:1, height:1, background:C.grijs100 },
  groepTeller:    { fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10 },
  vraagKaart:     { background:C.wit, border:`1px solid ${C.grijs100}`, borderRadius:9, marginBottom:7, cursor:'pointer', transition:'all .15s ease', boxShadow:'0 1px 3px rgba(0,0,0,.04)', overflow:'hidden' },
  vraagTop:       { display:'flex', alignItems:'flex-start', gap:13, padding:'14px 18px' },
  vraagNr:        { fontFamily:"'Lora',serif", fontSize:12, fontWeight:600, color:C.grijs200, minWidth:20, paddingTop:2 },
  vraagTekst:     { flex:1, fontSize:15, lineHeight:1.55, color:C.inkt, fontFamily:"'Lora',serif" },
  chevron:        { fontSize:13, color:C.grijs400, transition:'transform .2s ease', marginTop:3, flexShrink:0 },
  waaromBlok:     { borderTop:`1px solid ${C.grijs100}`, padding:'12px 18px 14px 51px', background:C.grijs50 },
  waaromLabel:    { display:'block', fontSize:10, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:5 },
  waaromTekst:    { fontSize:13, lineHeight:1.65, color:C.grijs600, fontStyle:'italic' },
};
